import { isValidReceivedMessage } from '@canvas/protocol/validate-received-message';

import { type RelaySupplierResult } from '../network/socket';
import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
  createCollabConnectionTransport,
} from '../transport';

import { type CollabStrategy } from './collab-strategy';

export class NcsCollabStrategy implements CollabStrategy {
  readonly type = 'ncs' as const;

  createTransport(ctx: TransportContext, callbacks: TransportCallbacks): Transport {
    return createCollabConnectionTransport(ctx, callbacks);
  }

  sendAwareness(
    message: Uint8Array<ArrayBuffer>,
    transport: Transport | undefined,
    fallbackSend: (msg: Uint8Array<ArrayBuffer>) => void,
  ): void {
    if (transport?.sendAwareness) {
      transport.sendAwareness(message);
    } else {
      fallbackSend(message);
    }
  }

  getStepsUrl(params: {
    documentId: string;
    headStep: string;
    serverUrl?: string;
    attempt: number;
  }): string {
    const { documentId, headStep, serverUrl } = params;
    if (!serverUrl) {
      throw new Error('serverUrl is required for NCS steps URL');
    }
    return `${serverUrl}/api/_internal/document/${encodeURIComponent(
      documentId,
    )}/steps/${headStep}`;
  }

  getRelayUrl(params: {
    boardId: string;
    attempt: number;
    resourceType: 'whiteboard' | 'database' | 'slide';
    tenantId: string;
    activationId: string;
  }): RelaySupplierResult {
    const { boardId, resourceType, tenantId, activationId } = params;
    const ariResourceType = resourceType === 'whiteboard' ? 'board' : resourceType;
    const ncsBasePath =
      typeof globalThis.location !== 'undefined'
        ? `${globalThis.location.protocol}//${globalThis.location.host}`
        : process.env.VITE_NCS_SESSION_URL;

    if (!ncsBasePath) {
      throw new Error('ncsBasePath is not defined.');
    }

    const isLocal = process.env.NODE_ENV === 'development';
    if (isLocal) {
      const sessionAri = `ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`;
      return {
        url: `${ncsBasePath}/ncs-canvas/session/${sessionAri}`,
      };
    }

    // use PMR in micros
    return {
      url: `${ncsBasePath}/ncs-canvas/${tenantId}/${activationId}/confluence`,
      namespace: `ncs-canvas/session/ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`,
    };
  }

  isReceivedMessageValid(message: unknown): boolean {
    // NCS peers send protocol messages directly to one another, so the client must
    // validate inbound payloads itself. CF historically relied on the DO path.
    return isValidReceivedMessage(message);
  }

  configureUpdateBatching({
    enableStateBridge,
  }: Parameters<CollabStrategy['configureUpdateBatching']>[0]): void {
    enableStateBridge();
  }

  sendConnectSync({
    canSync,
    closeConnectTimeGap,
  }: Parameters<CollabStrategy['sendConnectSync']>[0]) {
    if (canSync) {
      return closeConnectTimeGap();
    }
  }

  getSyncPhase1ReplyRecipient({
    recipient,
    sender,
  }: Parameters<CollabStrategy['getSyncPhase1ReplyRecipient']>[0]): string {
    return sender ?? recipient;
  }

  sendSyncPhase1Reply({
    message,
    sender,
    send,
    sendToPeer,
  }: Parameters<CollabStrategy['sendSyncPhase1Reply']>[0]): void {
    if (sender) {
      sendToPeer(message, sender);
      return;
    }
    send(message);
  }

  sendSyncPhase2Reply({
    message,
    recipient,
    send,
    sendToPeer,
  }: Parameters<CollabStrategy['sendSyncPhase2Reply']>[0]): void {
    if (recipient) {
      sendToPeer(message, recipient);
      return;
    }
    send(message);
  }

  shouldHandleLazySync(): boolean {
    return true;
  }

  sendLazySyncRequest({
    message,
    lastUpdateSender,
    send,
    sendToPeer,
  }: Parameters<CollabStrategy['sendLazySyncRequest']>[0]): void {
    if (lastUpdateSender) {
      sendToPeer(message, lastUpdateSender);
      return;
    }
    send(message);
  }

  shouldDeferReconnectUpdate(): boolean {
    return true;
  }

  prepareReconnectUpdateChanges({
    stateBridge,
  }: Parameters<CollabStrategy['prepareReconnectUpdateChanges']>[0]): void {
    stateBridge.reset();
    stateBridge.offUpdate();
  }

  restoreReconnectUpdateChanges({
    stateBridge,
    onUpdate,
  }: Parameters<CollabStrategy['restoreReconnectUpdateChanges']>[0]): void {
    stateBridge.onUpdate(onUpdate);
  }

  prepareReconnectUpdate({
    stateBridge,
    update,
    clientID,
    clock,
  }: Parameters<CollabStrategy['prepareReconnectUpdate']>[0]): void {
    stateBridge.resetAndLoad({
      payload: update,
      origin: undefined,
      clientID,
      clock,
    });
  }

  resetModeState({ stateBridge }: Parameters<CollabStrategy['resetModeState']>[0]): void {
    stateBridge.reset();
  }
}
